/**
 * Navigation catalog — the single source of truth for the app's primary
 * left-rail routes.
 *
 * This is intentionally a *data-only* description (no React, no icons) so the
 * navigation resolver can be unit-tested in isolation and so any consumer
 * (sidebar, settings checklist, post-login landing) reasons over the same
 * list. Icons stay in the sidebar, mapped by `id`.
 *
 * The `cap` field is the permission boundary: an item with a `cap` is only a
 * *candidate* for a user who passes `can(role, cap)` (see `@/lib/users`).
 * Items without a `cap` are visible to any authenticated user — matching the
 * historical sidebar, which gated almost nothing. Navigation preferences and
 * admin policy can only ever *hide* a candidate; they can never reveal an item
 * the user has no permission for. Hiding a row is never a security boundary —
 * every route stays protected server-side regardless of what the rail shows.
 *
 * Dynamic / action rows that aren't plain routes (Command Center toggle, the
 * per-agent roster, the New Issue button) are deliberately NOT in this catalog
 * — the sidebar owns those directly.
 */

/** Sidebar grouping. Order here is the order sections render in the rail. */
export const NAV_SECTIONS = ["overview", "work", "aiResults", "context", "aiOps", "platform"] as const;
export type NavSectionId = (typeof NAV_SECTIONS)[number];

export type NavCatalogItem = {
  /** Stable identifier — used in preferences/policy and the sidebar icon map. */
  id: string;
  /** i18n key resolved against the active locale (existing `nav.*` keys). */
  i18nKey: string;
  href: string;
  section: NavSectionId;
  /**
   * Permission action required to even be a candidate. Resolved with
   * `can(role, cap)`. Undefined → visible to any authenticated user.
   */
  cap?: string;
  /** Active-state prefix when broader than `href` (passed through to sidebar). */
  match?: string;
  /** Subtree excluded from the active match (passed through to sidebar). */
  exclude?: string;
  /** `g <letter>` keyboard chord hint, rendered by the sidebar. */
  gKey?: string;
};

/**
 * The catalog. `cap` values mirror the existing capability matrix in
 * `@/lib/users`. Only Governance + Governance Hierarchy carry a cap today —
 * they point at the admin-only governance console (the Settings → Governance
 * tab is already gated on `agents.manage`), so gating the rail rows the same
 * way makes the sidebar consistent with the tab. Everything else stays
 * uncapped exactly as the rail behaved before.
 */
export const NAV_CATALOG: readonly NavCatalogItem[] = [
  // Overview
  { id: "dashboard",     i18nKey: "nav.dashboard",     href: "/app",           section: "overview", gKey: "d" },
  { id: "inbox",         i18nKey: "nav.inbox",         href: "/inbox",         section: "overview", gKey: "n" },
  { id: "notifications", i18nKey: "nav.notifications", href: "/notifications", section: "overview" },

  // Work — what a person owns and starts from. A project is the prerequisite
  // for everything below it (an issue without one gets no environment
  // contract, and `buildOnboardingSteps` makes "create a project" the first
  // step of the generic checklist), so it leads the section.
  // No `gKey`: every free letter that reads as "projects" is already bound
  // (p = Patches, r = Runs), and the sidebar must never advertise a chord the
  // keyboard provider doesn't register (QA-L3).
  { id: "projects",    i18nKey: "nav.projects",    href: "/projects",    section: "work" },
  { id: "issues",      i18nKey: "nav.issues",      href: "/issues",      section: "work", gKey: "i" },
  { id: "followUps",   i18nKey: "nav.followUps",   href: "/follow-ups",  section: "work", gKey: "f" },

  // AI results — the four stages of one run (mission → workspace → patch →
  // sandbox verdict). They used to sit in Work, where seven flat rows read as
  // seven separate departments instead of stages of the same thing; all four
  // are also reachable from the issue detail.
  { id: "missions",    i18nKey: "nav.missions",    href: "/missions",    section: "aiResults", gKey: "m" },
  { id: "workspaces",  i18nKey: "nav.workspaces",  href: "/workspace",   section: "aiResults", gKey: "w" },
  { id: "patches",     i18nKey: "nav.patches",     href: "/patches",     section: "aiResults", gKey: "p" },
  { id: "sandboxRuns", i18nKey: "nav.sandboxRuns", href: "/sandbox-runs", section: "aiResults" },

  // Context
  // Contacts are reference material about who we talk to, not a work queue,
  // so they sit with knowledge rather than between issues and missions.
  // Uncapped like the work rows: every role holds `customer.read`
  // server-side; the /customers routes enforce it regardless.
  { id: "customerDirectory", i18nKey: "nav.customerDirectory", href: "/customers", section: "context" },
  { id: "knowledge",       i18nKey: "nav.knowledge",       href: "/knowledge",         section: "context", gKey: "k" },
  { id: "memory",          i18nKey: "nav.memory",          href: "/memory",            section: "context" },

  // AI Operations
  { id: "governance",          i18nKey: "nav.governance",          href: "/settings/governance", section: "aiOps", cap: "agents.manage", match: "/settings/governance" },
  { id: "governanceHierarchy", i18nKey: "nav.governanceHierarchy", href: "/governance/hierarchy", section: "aiOps", cap: "agents.manage", match: "/governance/hierarchy" },
  // Advanced/admin surface: inspect and tune how the orchestrator selects
  // agents, what context they receive, and which settings are safe to change.
  // Gated on the same cap as the governance rows — this is an operator console
  // for people who can act on what it shows.
  { id: "orchestrationConsole", i18nKey: "nav.orchestrationConsole", href: "/orchestration", section: "aiOps", cap: "agents.manage", match: "/orchestration", gKey: "o" },
  { id: "aiOperations",        i18nKey: "nav.aiOperations",        href: "/operations",          section: "aiOps", match: "/operations" },
  { id: "aiTeam",              i18nKey: "nav.aiTeam",              href: "/agents",              section: "aiOps" },
  // What the agents know how to do. Reading is open to any authenticated role
  // — an operator watching a run has to be able to see which skills shaped
  // it — so no `cap` gate here; the write paths carry their own.
  { id: "skills",              i18nKey: "nav.skills",              href: "/skills",              section: "aiOps", match: "/skills" },

  // Platform
  { id: "runs",     i18nKey: "nav.runs",     href: "/runs",                    section: "platform" },
  // Land on the settings overview — the novice landing that shows the chosen
  // goal, setup progress, what works and what's missing. (Historically this
  // pointed at Infrastructure, then Profile; the overview answers "where am I
  // and what's next?" for every audience.)
  { id: "settings", i18nKey: "nav.settings", href: "/settings/overview", section: "platform", match: "/settings", exclude: "/settings/governance" },
] as const;

/** All catalog ids — handy for validating/sanitising preference + policy input. */
export const NAV_ITEM_IDS: readonly string[] = NAV_CATALOG.map((i) => i.id);

/**
 * Task-oriented label overrides per work profile. A support agent thinks in
 * "customer questions" and "tickets", not "Inbox" and "Issues"; an engineer
 * thinks in "problems" and "proposed changes". Only labels change — ids,
 * routes and authorization are identical across profiles. Profiles without an
 * override (admin/custom/…) keep the neutral catalog label. Keys are plain
 * strings (not the WorkProfile type) to keep this module dependency-free.
 */
export const NAV_PROFILE_LABEL_KEYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  support: {
    dashboard: "nav.taskLabels.support.dashboard",
    inbox: "nav.taskLabels.support.inbox",
    issues: "nav.taskLabels.support.issues",
    knowledge: "nav.taskLabels.support.knowledge",
  },
  engineering: {
    issues: "nav.taskLabels.engineering.issues",
    missions: "nav.taskLabels.engineering.missions",
    patches: "nav.taskLabels.engineering.patches",
    sandboxRuns: "nav.taskLabels.engineering.sandboxRuns",
    workspaces: "nav.taskLabels.engineering.workspaces",
  },
};

/** The i18n key to render for an item under the given work profile. */
export function navLabelKeyForProfile(item: NavCatalogItem, profile: string): string {
  return NAV_PROFILE_LABEL_KEYS[profile]?.[item.id] ?? item.i18nKey;
}

const BY_ID: ReadonlyMap<string, NavCatalogItem> = new Map(NAV_CATALOG.map((i) => [i.id, i]));

export function navItemById(id: string): NavCatalogItem | undefined {
  return BY_ID.get(id);
}

export function isNavItemId(value: unknown): value is string {
  return typeof value === "string" && BY_ID.has(value);
}

/**
 * Items that are *always* a candidate to be shown regardless of the active
 * work profile or a user's hide list — never subject to profile/user hiding
 * (admin `hiddenNavItems` and missing permission still apply). Settings is the
 * escape hatch: if it could be hidden, a user who hid it could never reach the
 * preferences page to bring it back.
 */
export const ALWAYS_VISIBLE_IDS: ReadonlySet<string> = new Set(["settings"]);
